import { useState, useRef, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  BuildingsIcon,
  CaretDownIcon,
  GearIcon,
  MagnifyingGlassIcon,
  PlusIcon,
} from '@phosphor-icons/react';
import { Tooltip } from '../ui/Tooltip';
import type { UserCompanyDto } from '@monorepo/shared';

interface SidebarCompanySectionProps {
  companies: UserCompanyDto[];
  selectedCompanyId: string | null;
  selectedCompany: UserCompanyDto | undefined;
  isMainContactForSelectedCompany: boolean;
  setSelectedCompany: (id: string) => void;
}

export const SidebarCompanySection = ({
  companies,
  selectedCompanyId,
  selectedCompany,
  isMainContactForSelectedCompany,
  setSelectedCompany,
}: SidebarCompanySectionProps) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [search, setSearch] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  const filteredCompanies = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();

    if (!normalizedSearch) {
      return companies;
    }

    return companies.filter(
      (company) =>
        company.legalName.toLowerCase().includes(normalizedSearch) ||
        company.siren.toLowerCase().includes(normalizedSearch),
    );
  }, [companies, search]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      setSearch('');
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen]);

  if (companies.length === 0) return null;

  return (
    <div className="p-4">
      <div className="mb-2 px-2 text-xs font-semibold text-gray-500 uppercase">
        {t('sidebar.company')}
      </div>

      <div className="relative" ref={dropdownRef}>
        <button
          onClick={() => setIsDropdownOpen(!isDropdownOpen)}
          className="w-full flex items-center gap-3 px-3 py-2 rounded-lg border border-gray-300 hover:border-primary transition"
        >
          <BuildingsIcon size={20} weight="regular" className="text-primary" />
          <div className="flex-1 text-left">
            <div className="text-sm font-medium truncate">
              {selectedCompany?.legalName || t('sidebar.select_company')}
            </div>
            {selectedCompany && (
              <div className="text-xs text-gray-500">{selectedCompany.siren}</div>
            )}
          </div>
          <CaretDownIcon size={16} weight="regular" className="text-gray-500" />
        </button>

        {isDropdownOpen && (
          <div className="absolute bottom-full left-0 right-0 mb-2 flex flex-col bg-white rounded-lg shadow-lg border border-gray-200">
            <div className="relative flex-shrink-0 p-2">
              <MagnifyingGlassIcon
                size={16}
                className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"
              />
              <input
                type="text"
                value={search}
                autoFocus
                onChange={(event) => setSearch(event.target.value)}
                placeholder={t('sidebar.search_company')}
                className="w-full pl-8 pr-2 py-1.5 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>

            <div className="flex flex-col max-h-64 overflow-y-auto">
              {filteredCompanies.length === 0 && (
                <p className="px-3 py-3 text-sm text-gray-500">{t('sidebar.no_company_found')}</p>
              )}

              {filteredCompanies.map((company) => (
                <button
                  key={company.id}
                  onClick={() => {
                    setSelectedCompany(company.id);
                    setIsDropdownOpen(false);
                  }}
                  className={`w-full flex items-center gap-3 px-3 py-2 hover:bg-background transition ${
                    selectedCompanyId === company.id ? 'bg-primary bg-opacity-10' : ''
                  }`}
                >
                  <BuildingsIcon
                    size={20}
                    weight={selectedCompanyId === company.id ? 'fill' : 'regular'}
                    className="text-primary"
                  />
                  <div className="flex-1 text-left">
                    <div className="text-sm font-medium truncate">{company.legalName}</div>
                    <div className="text-xs text-gray-500">{company.siren}</div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      <Tooltip
        content={t('sidebar.settings_disabled_tooltip')}
        disabled={isMainContactForSelectedCompany}
      >
        <button
          onClick={() => selectedCompanyId && navigate(`/company/${selectedCompanyId}/settings`)}
          disabled={!isMainContactForSelectedCompany || !selectedCompanyId}
          className="w-full flex items-center gap-3 px-3 py-2 mt-3 text-text hover:bg-background rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <GearIcon size={20} weight="regular" className="text-primary" />
          <span className="text-sm font-medium">{t('sidebar.settings')}</span>
        </button>
      </Tooltip>

      <button
        onClick={() => navigate('/company-settings')}
        className="w-full flex items-center gap-3 px-3 py-2 mt-1 text-text hover:bg-background rounded-lg transition"
      >
        <PlusIcon size={20} weight="regular" className="text-primary" />
        <span className="text-sm font-medium">{t('sidebar.add_company')}</span>
      </button>
    </div>
  );
};
